import React, { useCallback, useEffect } from "react";
import ReactGA from "react-ga4";
import { toast } from "react-toastify";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useHistory, useLocation, useParams } from "react-router-dom";

import "../campaign.scss";
import Title from "../../../../uiKit/Title/Title";
import CustomButton from "../../../../uiKit/Button/CustomButton";
import { BlueMail } from "../../../../uiKit/assets/svg/icons";
import { GreyPenSmall } from "../../../../uiKit/assets/svg/icons";
import AnimatedLoading from "../../../../uiKit/Loading/animatedLoading";
import { blue300, gray650 } from "../../../../uiKit/assets/colors/colors";
import RecentDonationsTable from "../RecentDonations/recentDonationsTable";
import Badge from "./campaignBadge";
import CampaignBalanceInfo from "./campaignBalanceInfo";
import { Breadcrumb } from "../../../../components/Breadcrumbs/breadcrumbs";
import DonationAmount from "../donationAmount";

import { getCampaign } from "../../../../redux/state/campaignSlice";
import { getMember } from "../../../../redux/state/memberSlice";
import ProgressBar from "../../../../uiKit/ProgressBar/ProgressBar";
import Transfer from "../transfer";

const ActiveCampaign = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const { campaignId, communityId } = useParams();

  const screenWidth = useSelector((state) => state.common.screenWidth, shallowEqual);
  const campaign = useSelector((state) => state.campaign.campaign, shallowEqual);
  const isLoading = useSelector((state) => state.campaign.isLoading.common, shallowEqual);
  const isLoadingDonation = useSelector(
    (state) => state.campaign.isLoading.campaignDonations,
    shallowEqual
  );
  const isAdmin = useSelector((state) => state.community.community.role, shallowEqual);

  useEffect(() => {
    if (!communityId || !campaignId) return;
    dispatch(getCampaign({ communityId, campaignId }))
      .then(unwrapResult)
      .catch(({ status }) => {
        if (status === 404) {
          history.push(`/campaigns/campaign-completed/${communityId}/${campaignId}`);
        }
      });
  }, [communityId, campaignId, dispatch, history]);

  const onRecipientClick = () => {
    if (!!campaign.recipient && !!campaign.recipient.id) {
      dispatch(getMember({ communityId, userId: campaign.recipient.id }))
        .then(unwrapResult)
        .then(() =>
          history.push(`/communities/community/${communityId}/member/${campaign.recipient.id}`)
        )
        .catch(() => toast.info("User is not a member of this community"));
    }
  };

  const onCommunityClick = () => {
    history.push(`/communities/community/${communityId}`);
  };

  const inviteFriendHandler = useCallback(() => {
    ReactGA.event("invite_friend_to_donate");
    history.push(`/campaigns/campaign/${communityId}/${campaignId}/invite-friend`);
  }, [history, campaignId, communityId]);

  const navigateToActiveCampaignEdit = () => {
    history.push(`${pathname}/edit`);
  };

  return (
    <>
      <Breadcrumb title={campaign.name} />
      <div className="campaignInfoHeaderWrapper">
        <div style={{ position: "relative" }}>
          <img
            className="photoWrapperCampaign"
            alt=""
            src={screenWidth < 900 ? campaign.images.mobile : campaign.images.desktop}
          />
          <Badge campaignUrgencyType={campaign.campaignUrgencyType} />
        </div>
        <div className="recipient">
          <Title
            text={campaign.name}
            fontSize={screenWidth > 1200 ? 32 : 24}
            lineHeight={screenWidth > 1200 ? 40 : 32}
            className="ourBalanceTitle"
          />
          {!!isAdmin && (
            <div className="menuItem" onClick={navigateToActiveCampaignEdit}>
              <GreyPenSmall />
            </div>
          )}
        </div>
        {!!isAdmin && <CampaignBalanceInfo />}
        {!isAdmin && !isLoadingDonation && (
          <ProgressBar
            campaignId={campaign.id}
            communityId={campaign.communityId}
            target={campaign.amount}
            isActive={campaign.status === "launched"}
            className=""
          />
        )}
        {!!isAdmin && !!campaign && (
          <div>
            {!!campaign.recipient && (
              <div className="recipient">
                <Title text="Recipient:" fontSize={16} color={gray650} />
                <Title
                  text={campaign.recipient.fullName}
                  fontSize={16}
                  color={blue300}
                  style={{ marginLeft: "16px", cursor: "pointer" }}
                  onClick={onRecipientClick}
                />
              </div>
            )}
            {!!campaign.communityName && (
              <div className="recipient">
                <Title text="Community:" fontSize={16} color={gray650} />
                <Title
                  text={campaign.communityName}
                  fontSize={16}
                  color={blue300}
                  style={{ marginLeft: "16px", cursor: "pointer" }}
                  onClick={onCommunityClick}
                />
              </div>
            )}
          </div>
        )}
        {!!campaign.description ? (
          <Title
            text={campaign.description}
            style={{ marginBottom: "24px", marginTop: "20px" }}
            fontWeight={400}
          />
        ) : (
          <div style={{ height: "8px" }} />
        )}
        <div className="transferButtons">
          <DonationAmount />
          {!!isAdmin && <Transfer />}
          <CustomButton
            title="Invite friend to donate"
            isWhite
            Icon={BlueMail}
            classNames="margin16to0"
            onClick={inviteFriendHandler}
          />
        </div>
        <AnimatedLoading loading={isLoading} />
      </div>
      <div className="campaignsWrapper">
        <Title text="Recent Donations" fontSize={24} lineHeight={32} className="recentHeader" />
        <RecentDonationsTable isAdmin={!!isAdmin} />
      </div>
    </>
  );
};

export default ActiveCampaign;
